import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Mail,
  MapPin,
  Cpu,
  Lock,
  CreditCard,
  HeartHandshake,
  Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";

const SUPPORT_EMAIL = "support@fitauraai.com";
const LOCATION = "​";

const About = () => {
  const navigate = useNavigate();

  // SEO handled by <SEO /> component below.

  return (
    <>
      <SEO />
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
        <div className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-primary/5 blur-[140px]" />
      </div>

      <header className="sticky top-0 z-10 border-b border-white/10 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-5 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-base font-bold uppercase tracking-[0.18em]">About</h1>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-5 py-6 pb-20">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_0_24px_-6px_hsl(var(--primary))]">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
              — About FitAura
            </span>
            <h2 className="font-display mt-0.5 text-2xl font-bold uppercase tracking-tight">
              The studio in your pocket.
            </h2>
            <p className="text-xs text-muted-foreground">Made in India · Used worldwide</p>
          </div>
        </div>

        <Card className="space-y-6 p-5 shadow-soft">
          <section>
            <h3 className="mb-2 text-base font-bold">Who we are</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              FitAura AI is a virtual fitting room built for the way people actually shop in 2026 — fast,
              visual, and obsessed with how things look on <span className="text-foreground font-medium">you</span>,
              not on a model. Drop any product link, upload a photo, and our generative engine drapes the garment
              onto your body with photoreal fabric, lighting and pose.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We're a small team of stylists, ML engineers and former e-commerce operators based in Bangalore,
              India 🇮🇳, with collaborators across Berlin and NYC. Our mission: cut the 30% online return
              rate to zero — one perfect fit at a time.
            </p>
          </section>

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                icon: HeartHandshake,
                k: "Mission",
                v: "Help every shopper see themselves before they buy. No more guessing.",
              },
              {
                icon: Cpu,
                k: "Engine",
                v: "Built on Nano Banana 2 — a multimodal diffusion model trained on 40M+ garment-on-body pairs.",
              },
              {
                icon: Lock,
                k: "Privacy",
                v: "Your photos are encrypted, never used for training, and deletable in one tap.",
              },
              {
                icon: CreditCard,
                k: "Pricing",
                v: "Pay-as-you-go credits — starting at $2.99. No subscriptions. 1 credit = 1 try-on · 0.10 credits = 1 Aura Score · results auto-saved to your phone (Android).",
              },
              {
                icon: Globe2,
                k: "Payments",
                v: "Google Play Billing on Android · UPI, cards & netbanking for India · cards, Apple/Google Pay worldwide on web. Taxes & GST included.",
              },
              {
                icon: Mail,
                k: "Support",
                v: "Email support within 24h on every paid pack. Pro packs get priority queue.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.k}
                  className="rounded-xl border border-border bg-card/40 p-4 transition-colors hover:border-primary/40"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <Icon className="h-4 w-4" />
                    <p className="font-display text-[10px] font-bold uppercase tracking-[0.25em]">
                      {item.k}
                    </p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/85">{item.v}</p>
                </div>
              );
            })}
          </div>

          <section className="rounded-xl border border-primary/20 bg-primary/5 p-5">
            <h3 className="mb-3 flex items-center gap-2 text-base font-bold">
              <Mail className="h-4 w-4 text-primary" /> Contact us
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-medium">Support email</p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    For account, billing, refunds, or feedback. We reply within 24 hours.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-muted-foreground">{LOCATION}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    FitAura AI  · operated from India.
                  </p>
                </div>
              </li>
            </ul>
          </section>

          <section className="flex flex-wrap gap-2 pt-1 text-xs">
            <Link
              to="/privacy"
              className="rounded-full border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="rounded-full border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              Terms of Service
            </Link>
            <Link
              to="/refund"
              className="rounded-full border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              Refund Policy
            </Link>
          </section>
        </Card>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-6" />
      </main>
    </div>
    </>
  );
};

export default About;
